/**
 * Validazione età partecipanti per iscrizioni a squadre (es. danza per categoria).
 */

const AGE_LABEL_REGEX = /\((\d+)\s*[-–]\s*(\d+)(?:\s*anni?)?\)/i;
export const DEFAULT_OUT_OF_QUOTA = 2;

export function normalizeOption(option) {
  if (typeof option === 'string') {
    const label = option.trim();
    const value = label;
    const match = label.match(AGE_LABEL_REGEX);
    if (match) {
      return {
        value,
        label,
        minAge: parseInt(match[1], 10),
        maxAge: parseInt(match[2], 10),
        maxOutOfQuota: DEFAULT_OUT_OF_QUOTA
      };
    }
    return { value, label, minAge: null, maxAge: null, maxOutOfQuota: 0 };
  }

  if (option && typeof option === 'object') {
    const label = (option.label || option.value || '').trim();
    const value = (option.value || option.label || '').trim();
    let minAge = option.minAge != null ? Number(option.minAge) : null;
    let maxAge = option.maxAge != null ? Number(option.maxAge) : null;

    if (minAge == null || maxAge == null) {
      const match = label.match(AGE_LABEL_REGEX);
      if (match) {
        minAge = minAge ?? parseInt(match[1], 10);
        maxAge = maxAge ?? parseInt(match[2], 10);
      }
    }

    const hasRange = minAge != null && maxAge != null && !Number.isNaN(minAge) && !Number.isNaN(maxAge);
    const maxOutOfQuota = option.maxOutOfQuota != null
      ? Number(option.maxOutOfQuota)
      : (hasRange ? DEFAULT_OUT_OF_QUOTA : 0);

    return {
      value,
      label,
      minAge: hasRange ? minAge : null,
      maxAge: hasRange ? maxAge : null,
      maxOutOfQuota: Number.isNaN(maxOutOfQuota) ? 0 : maxOutOfQuota
    };
  }

  const str = String(option);
  return { value: str, label: str, minAge: null, maxAge: null, maxOutOfQuota: 0 };
}

export function getOptionValue(option) {
  return normalizeOption(option).value;
}

export function getOptionLabel(option) {
  return normalizeOption(option).label;
}

export function computeAge(birthDate, referenceDate = new Date()) {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  const ref = referenceDate instanceof Date ? referenceDate : new Date(referenceDate);
  if (Number.isNaN(birth.getTime()) || Number.isNaN(ref.getTime())) return null;

  let age = ref.getFullYear() - birth.getFullYear();
  const monthDiff = ref.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && ref.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age;
}

function isAgeInRange(age, minAge, maxAge) {
  return age >= minAge && age <= maxAge;
}

export function getActiveAgeRules(registrationFields, formFields, eventCategory) {
  if (!Array.isArray(registrationFields) || !formFields) return null;

  for (const field of registrationFields) {
    if (field.fieldType !== 'select') continue;

    const enforceRules = field.enforceAgeRules === true
      || (eventCategory === 'danza' && field.fieldName === 'ageBand');

    if (!enforceRules) continue;

    const selectedValue = formFields[field.fieldName];
    if (!selectedValue) continue;

    const options = field.options || [];
    for (const opt of options) {
      const normalized = normalizeOption(opt);
      if (normalized.value === selectedValue && normalized.minAge != null && normalized.maxAge != null) {
        
        let dynamicMaxOutOfQuota = normalized.maxOutOfQuota;
        
        // Regole dinamiche in base alla configurazione (Solisti, Duo, Gruppi)
        const perfType = formFields['category'];
        if (perfType === 'Solisti') {
          dynamicMaxOutOfQuota = 0;
        } else if (perfType === 'Duo/Passo a due') {
          dynamicMaxOutOfQuota = Math.min(1, normalized.maxOutOfQuota);
        }

        return {
          fieldName: field.fieldName,
          fieldLabel: field.fieldLabel || field.fieldName,
          categoryLabel: normalized.label,
          minAge: normalized.minAge,
          maxAge: normalized.maxAge,
          maxOutOfQuota: dynamicMaxOutOfQuota
        };
      }
    }
  }

  return null;
}

function classifyMembers(members, rules, referenceDate) {
  let inQuota = 0;
  let outOfQuota = 0;
  const missingBirthDate = [];
  const outOfRangeMembers = [];

  for (const member of members) {
    const age = computeAge(member.birth_date, referenceDate);
    if (age == null) {
      missingBirthDate.push(member);
      continue;
    }

    if (isAgeInRange(age, rules.minAge, rules.maxAge)) {
      inQuota += 1;
    } else {
      outOfQuota += 1;
      outOfRangeMembers.push({ member, age });
    }
  }

  return { inQuota, outOfQuota, missingBirthDate, outOfRangeMembers };
}

export function validateTeamMemberAges({
  members,
  registrationFields,
  formFields,
  eventCategory,
  referenceDate
}) {
  const rules = getActiveAgeRules(registrationFields, formFields, eventCategory);
  if (!rules) {
    return { valid: true, rules: null };
  }

  const refDate = referenceDate || new Date();
  const {
    inQuota,
    outOfQuota,
    missingBirthDate,
    outOfRangeMembers
  } = classifyMembers(members, rules, refDate);

  if (missingBirthDate.length > 0) {
    const names = missingBirthDate
      .map(m => `${m.first_name || ''} ${m.last_name || ''}`.trim() || 'Partecipante')
      .join(', ');
    return {
      valid: false,
      rules,
      inQuota,
      outOfQuota,
      message: `Data di nascita mancante per: ${names}. Impossibile verificare l'età per la categoria "${rules.categoryLabel}".`
    };
  }

  if (outOfQuota > rules.maxOutOfQuota) {
    const extra = outOfRangeMembers
      .slice(rules.maxOutOfQuota)
      .map(({ member, age }) => `${member.first_name} ${member.last_name} (${age} anni)`)
      .join(', ');
    return {
      valid: false,
      rules,
      inQuota,
      outOfQuota,
      message: `Per "${rules.categoryLabel}" sono ammessi al massimo ${rules.maxOutOfQuota} partecipanti fuori fascia (${rules.minAge}-${rules.maxAge} anni). `
        + `Attualmente ${outOfQuota} fuori fascia${extra ? `: ${extra}` : ''}.`
    };
  }

  return { valid: true, rules, inQuota, outOfQuota };
}

export function canAddMemberToTeam(member, currentMembers, rules, referenceDate) {
  if (!rules) return { allowed: true };

  const age = computeAge(member.birth_date, referenceDate);
  if (age == null) {
    return {
      allowed: false,
      reason: 'Data di nascita non disponibile: impossibile verificare l\'età.'
    };
  }

  if (isAgeInRange(age, rules.minAge, rules.maxAge)) {
    return { allowed: true, status: 'in_quota' };
  }

  const { outOfQuota } = classifyMembers(currentMembers, rules, referenceDate);
  if (outOfQuota >= rules.maxOutOfQuota) {
    return {
      allowed: false,
      status: 'out_of_quota',
      reason: `Hai già raggiunto il massimo di ${rules.maxOutOfQuota} partecipanti fuori fascia (${rules.minAge}-${rules.maxAge} anni).`
    };
  }

  return { allowed: true, status: 'out_of_quota_slot' };
}

export function getMemberAgeStatus(member, rules, referenceDate) {
  if (!rules) return 'unknown';
  const age = computeAge(member.birth_date, referenceDate);
  if (age == null) return 'no_birth_date';
  if (isAgeInRange(age, rules.minAge, rules.maxAge)) return 'in_quota';
  return 'out_of_quota';
}

export function getEventReferenceDate(event) {
  if (!event) return new Date();
  const raw = event.eventDate || event.event_date;
  if (!raw) return new Date();
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? new Date() : d;
}
